/**
 * ImpersonateTab Component
 *
 * Dev-only panel for searching users and impersonating them from localhost.
 * Searches across the current org context and applies JWT cookies directly.
 */

import React, { useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    FiUsers,
    FiSearch,
    FiLogIn,
    FiLoader,
    FiAlertTriangle
} from 'react-icons/fi';

const ImpersonateTab = ({
    getApiUrl,
    getCookie,
    apiRequest,
    currentOrgId,
    onShowNotification
}) => {
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [impersonating, setImpersonating] = useState(null);
    const searchTimeout = useRef(null);

    const searchUsers = useCallback(async (q) => {
        if (q.length < 2) {
            setResults([]);
            return;
        }

        setSearching(true);
        try {
            const orgParam = currentOrgId && currentOrgId !== 'local-dev'
                ? `&org=${currentOrgId}`
                : '';
            const response = await apiRequest(`/api/dev/users/search?q=${encodeURIComponent(q)}${orgParam}`);
            if (response.ok) {
                const data = await response.json();
                setResults(data.users || []);
            } else {
                setResults([]);
            }
        } catch (err) {
            console.error('User search failed:', err);
            setResults([]);
        } finally {
            setSearching(false);
        }
    }, [apiRequest, currentOrgId]);

    const handleQueryChange = useCallback((e) => {
        const val = e.target.value;
        setQuery(val);

        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => searchUsers(val), 300);
    }, [searchUsers]);

    const impersonateUser = useCallback(async (user) => {
        setImpersonating(user.id);
        try {
            const baseUrl = getApiUrl();
            const response = await fetch(`${baseUrl}/api/dev/impersonate`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': getCookie('csrf_access_token')
                },
                body: JSON.stringify({
                    user_id: user.id,
                    organization_id: user.organization_id || currentOrgId
                })
            });

            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Impersonation failed');
            }

            const data = await response.json();

            if (data.success) {
                // Store org ID if switching orgs
                if (data.organization_id && data.organization_id !== 'main') {
                    localStorage.setItem('devOrgSubdomain', data.organization_id);
                }

                onShowNotification(`Impersonating ${data.username}... Refreshing.`, 'success');

                // Reload the page to pick up the new session (cookies are set by server)
                setTimeout(() => {
                    window.location.href = '/';
                }, 500);
            }
        } catch (err) {
            onShowNotification(`Failed: ${err.message}`, 'error');
            setImpersonating(null);
        }
    }, [getApiUrl, getCookie, currentOrgId, onShowNotification]);

    const getInitials = (user) => {
        if (user.first_name && user.last_name) {
            return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
        }
        return (user.username || '?')[0].toUpperCase();
    };

    return (
        <div className="dos-settings-section">
            <div className="dos-section-header">
                <FiUsers className="dos-section-icon" />
                <span className="dos-section-title">Impersonate User</span>
            </div>

            {/* Warning banner */}
            <div className="dos-impersonate-warning">
                <FiAlertTriangle className="dos-impersonate-warning-icon" />
                <span>
                    This will log you in as the selected user. Your current session will end.
                </span>
            </div>

            {/* Search input */}
            <div className="dos-impersonate-search">
                <FiSearch className="dos-impersonate-search-icon" />
                <input
                    type="text"
                    className="dos-impersonate-search-input"
                    placeholder="Search by name, username, or email..."
                    value={query}
                    onChange={handleQueryChange}
                    autoFocus
                />
                {searching && <FiLoader className="dos-impersonate-spinner" />}
            </div>

            {/* Results */}
            <div className="dos-impersonate-results">
                {results.length === 0 && query.length >= 2 && !searching && (
                    <div className="dos-impersonate-empty">
                        No users found matching "{query}"
                    </div>
                )}

                {results.length === 0 && query.length < 2 && (
                    <div className="dos-impersonate-empty">
                        Type at least 2 characters to search
                    </div>
                )}

                {results.map((user) => (
                    <div key={`${user.id}-${user.organization_id || 'main'}`} className="dos-impersonate-user">
                        <div className="dos-impersonate-avatar">
                            {getInitials(user)}
                        </div>
                        <div className="dos-impersonate-user-info">
                            <div className="dos-impersonate-user-name">
                                {user.first_name && user.last_name
                                    ? `${user.first_name} ${user.last_name}`
                                    : user.username
                                }
                            </div>
                            <div className="dos-impersonate-user-meta">
                                {user.username} {user.email ? `\u00B7 ${user.email}` : ''}
                            </div>
                            {user.org_name && (
                                <div className="dos-impersonate-user-org">{user.org_name}</div>
                            )}
                        </div>
                        <button
                            className="dos-impersonate-btn"
                            onClick={() => impersonateUser(user)}
                            disabled={impersonating === user.id}
                        >
                            {impersonating === user.id
                                ? <FiLoader className="dos-impersonate-spinner" />
                                : <FiLogIn />
                            }
                            {impersonating === user.id ? 'Loading...' : 'Impersonate'}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

ImpersonateTab.propTypes = {
    getApiUrl: PropTypes.func.isRequired,
    getCookie: PropTypes.func.isRequired,
    apiRequest: PropTypes.func.isRequired,
    currentOrgId: PropTypes.string,
    onShowNotification: PropTypes.func.isRequired
};

export default ImpersonateTab;
